/**
 * R2-Phase-3 — Shared theming helpers for folders (colour + icon palettes).
 * Tailwind needs the literal class names somewhere it can scan; keeping them
 * inline in a helper module is the simplest approach.
 */
import React from "react";
import {
  Briefcase,
  Flag,
  Folder,
  FolderHeart,
  FolderKanban,
  FolderOpen,
  FolderTree,
  Rocket,
  Star,
  Target,
  Trophy,
  Users,
} from "lucide-react";

export const FOLDER_COLORS = [
  "slate",
  "indigo",
  "violet",
  "rose",
  "emerald",
  "amber",
  "sky",
  "fuchsia",
  "cyan",
  "lime",
  "orange",
  "red",
];

export const FOLDER_ICONS = [
  "folder",
  "folder-open",
  "folder-heart",
  "folder-kanban",
  "folder-tree",
  "briefcase",
  "rocket",
  "star",
  "flag",
  "target",
  "users",
  "trophy",
];

const ICON_MAP = {
  folder: Folder,
  "folder-open": FolderOpen,
  "folder-heart": FolderHeart,
  "folder-kanban": FolderKanban,
  "folder-tree": FolderTree,
  briefcase: Briefcase,
  rocket: Rocket,
  star: Star,
  flag: Flag,
  target: Target,
  users: Users,
  trophy: Trophy,
};

export function FolderIcon({ name, size = 16, className = "" }) {
  const Cmp = ICON_MAP[name] || Folder;
  return <Cmp size={size} className={className} aria-hidden />;
}

/**
 * Pre-baked Tailwind classes per palette so the JIT compiler sees them.
 * Keep this list aligned with FOLDER_COLORS above.
 */
const PALETTE = {
  slate: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", accent: "bg-slate-500" },
  indigo: { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300", accent: "bg-indigo-500" },
  violet: { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-300", accent: "bg-violet-500" },
  rose: { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-300", accent: "bg-rose-500" },
  emerald: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300", accent: "bg-emerald-500" },
  amber: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300", accent: "bg-amber-500" },
  sky: { bg: "bg-sky-100", text: "text-sky-700", border: "border-sky-300", accent: "bg-sky-500" },
  fuchsia: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", border: "border-fuchsia-300", accent: "bg-fuchsia-500" },
  cyan: { bg: "bg-cyan-100", text: "text-cyan-700", border: "border-cyan-300", accent: "bg-cyan-500" },
  lime: { bg: "bg-lime-100", text: "text-lime-700", border: "border-lime-300", accent: "bg-lime-500" },
  orange: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300", accent: "bg-orange-500" },
  red: { bg: "bg-red-100", text: "text-red-700", border: "border-red-300", accent: "bg-red-500" },
};

export function folderColorClasses(color) {
  return PALETTE[color] || PALETTE.slate;
}
